/**
 * snap.js — Edge & sibling snapping for window drag/resize.
 *
 * Snap candidates: viewport left/top (0), viewport right/bottom, plus the
 * left/right/top/bottom edges of every other open window in the active
 * dashboard. Threshold is in CSS pixels.
 *
 * Hold Shift while dragging/resizing to bypass snapping (callers gate this).
 */

const THRESHOLD = 8;

function lines(others, viewport) {
  const xs = [0, viewport.w];
  const ys = [0, viewport.h];
  for (const w of others) {
    if (w.minimized || w.maximized) continue;
    xs.push(w.x, w.x + w.w);
    ys.push(w.y, w.y + w.h);
  }
  return { xs, ys };
}

function nearest(value, candidates, threshold = THRESHOLD) {
  let best = null, bestDist = threshold + 1;
  for (const c of candidates) {
    const d = Math.abs(value - c);
    if (d < bestDist) { bestDist = d; best = c; }
  }
  return best;
}

export function snapDrag(box, others, viewport) {
  const { xs, ys } = lines(others, viewport);
  // Prefer snapping the leading edge in the direction of motion; fall back
  // to the opposite edge. Simpler: try left first, then right.
  const sl = nearest(box.x, xs);
  if (sl != null) box.x = sl;
  else {
    const sr = nearest(box.x + box.w, xs);
    if (sr != null) box.x = sr - box.w;
  }
  const st = nearest(box.y, ys);
  if (st != null) box.y = st;
  else {
    const sb = nearest(box.y + box.h, ys);
    if (sb != null) box.y = sb - box.h;
  }
  return box;
}

export function snapResize(box, handle, others, viewport, minW = 220, minH = 140) {
  const { xs, ys } = lines(others, viewport);
  if (handle.includes('e')) {
    const s = nearest(box.x + box.w, xs);
    if (s != null) box.w = Math.max(minW, s - box.x);
  }
  if (handle.includes('w')) {
    const s = nearest(box.x, xs);
    if (s != null) {
      const right = box.x + box.w;
      box.x = Math.min(s, right - minW);
      box.w = right - box.x;
    }
  }
  if (handle.includes('s')) {
    const s = nearest(box.y + box.h, ys);
    if (s != null) box.h = Math.max(minH, s - box.y);
  }
  if (handle.includes('n')) {
    const s = nearest(box.y, ys);
    if (s != null) {
      const bottom = box.y + box.h;
      box.y = Math.min(s, bottom - minH);
      box.h = bottom - box.y;
    }
  }
  return box;
}

export function viewportSize() {
  // Use the desktop container as the usable viewport. Its CSS top/bottom
  // already reflects top chrome offsets and the docked taskbar (#382).
  if (typeof document !== 'undefined') {
    const desktop = document.querySelector('.v2-desktop');
    const rect = desktop?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      return { w: rect.width, h: rect.height };
    }
  }
  if (typeof window === 'undefined') return { w: 1200, h: 752 };
  return { w: window.innerWidth, h: Math.max(0, window.innerHeight - 48) };
}

// ── #5: Windows-style edge / corner snap ─────────────────────────────────
// Driven by the live pointer position during a drag (not the window box) so
// it matches the OS behaviour: shove the cursor at a screen edge to half-snap,
// the top to maximise, a corner to quarter-snap. `viewport` is the usable
// area (taskbar already subtracted via viewportSize()).
const EDGE_TRIGGER   = 22;   // px from an edge to fire a half / maximise snap
const CORNER_TRIGGER = 96;   // px corner box that fires a quarter snap

export function edgeSnapZone(pointer, viewport) {
  const x = pointer.x, y = pointer.y;
  const W = viewport.w, H = viewport.h;
  if (W <= 0 || H <= 0) return null;
  const halfW = Math.round(W / 2);
  const halfH = Math.round(H / 2);

  const nearL = x <= CORNER_TRIGGER;
  const nearR = x >= W - CORNER_TRIGGER;
  const nearT = y <= CORNER_TRIGGER;
  const nearB = y >= H - CORNER_TRIGGER;

  // Quarter snaps win when the cursor is parked in a corner box.
  if (nearT && nearL) return { zone: 'tl', rect: { x: 0,     y: 0,     w: halfW,     h: halfH },     maximize: false };
  if (nearT && nearR) return { zone: 'tr', rect: { x: halfW, y: 0,     w: W - halfW, h: halfH },     maximize: false };
  if (nearB && nearL) return { zone: 'bl', rect: { x: 0,     y: halfH, w: halfW,     h: H - halfH }, maximize: false };
  if (nearB && nearR) return { zone: 'br', rect: { x: halfW, y: halfH, w: W - halfW, h: H - halfH }, maximize: false };

  // Edge snaps — tighter threshold so they only fire right at the border.
  if (y <= EDGE_TRIGGER)     return { zone: 'max',   rect: null,                                   maximize: true  };
  if (x <= EDGE_TRIGGER)     return { zone: 'left',  rect: { x: 0,     y: 0, w: halfW,     h: H }, maximize: false };
  if (x >= W - EDGE_TRIGGER) return { zone: 'right', rect: { x: halfW, y: 0, w: W - halfW, h: H }, maximize: false };
  return null;
}

